import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldAlert } from "lucide-react";
import { Logo } from "@/components/Logo";
import { API } from "@/lib/api";
import { EMPLOYEES, DEPARTMENTS } from "@/data/seed";

// Google "G" mark (inline SVG so no external asset is needed).
function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 4.5 29.4 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5 45.5 35.9 45.5 24c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 4.5 29.4 2.5 24 2.5 16.3 2.5 9.7 6.8 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 45.5c5.3 0 10.1-2 13.7-5.3l-6.3-5.3c-2 1.5-4.6 2.4-7.4 2.4-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 41.1 16.2 45.5 24 45.5z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3C41.9 35.5 45.5 30.3 45.5 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const denied = params.get("denied") === "1";
  const errored = params.get("error") === "1";
  const [checking, setChecking] = useState(true);

  // Remember me: if a valid session (or guest) already exists, skip the login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (localStorage.getItem("iq-guest") === "1") { navigate("/hq", { replace: true }); return; }
      } catch (_) {}
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (r.ok && !cancelled) { navigate("/hq", { replace: true }); return; }
      } catch (_) {}
      if (!cancelled) setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/hq";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exploreDemo = () => { try { localStorage.setItem("iq-guest", "1"); } catch (_) {} navigate("/hq"); };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#030A16]">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#00E5FF]/30 border-t-[#00E5FF]" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030A16] bg-radials grain">
      <div className="bg-grid absolute inset-0 opacity-40" />
      <div className="relative z-10 mx-auto grid min-h-screen max-w-6xl grid-cols-1 items-center gap-10 px-6 py-10 lg:grid-cols-2">
        {/* Left: sign-in */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <Logo size={52} withWordmark subtitle="Intelligent Interactive Agent Solutions" />
          <h1 className="mt-8 font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Hire the intelligence.<br /><span className="neon-text text-[#00E5FF]">Keep the control.</span>
          </h1>
          <p className="mt-4 max-w-md text-slate-400">AI associates trained for the way your business actually works.</p>

          <div className="mt-8 max-w-md space-y-3">
            {denied && (
              <div className="flex items-start gap-2 rounded-xl border border-[#FF0055]/30 bg-[#FF0055]/8 px-3 py-2.5 text-sm text-[#FF9BB4]" data-testid="signin-denied" role="alert">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                That Google account isn't authorized for IQ Associates. Ask an admin to add you, or use Explore Demo.
              </div>
            )}
            {errored && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-slate-300" data-testid="signin-autherror" role="alert">
                Sign-in didn't complete. Please try again.
              </div>
            )}

            <button onClick={login} className="flex w-full items-center justify-center gap-3 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#1F2937] transition-transform hover:-translate-y-0.5" data-testid="signin-google">
              <GoogleIcon /> Continue with Google
            </button>
            <button onClick={exploreDemo} className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-200 transition-colors hover:border-[#00E5FF]/40 hover:text-white" data-testid="signin-explore-demo">
              <Sparkles size={15} /> Explore Demo — no sign-in needed
            </button>
            <p className="pt-1 text-xs text-slate-600">Access is limited to authorized team accounts. You'll stay signed in on this device for 7 days.</p>
          </div>
        </motion.div>

        {/* Right: visual preview of the digital building */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative hidden lg:block">
          <div className="glass-strong relative overflow-hidden rounded-3xl p-6">
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(138,43,226,0.3), transparent 70%)" }} />
            <div className="mb-4 flex items-center justify-between">
              <div className="text-xs font-mono uppercase tracking-wide text-slate-400">Digital Headquarters · preview</div>
              <span className="flex items-center gap-1.5 text-xs text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> live</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {DEPARTMENTS.slice(0, 4).map((d, i) => (
                <motion.div key={d.id} className="rounded-2xl border border-white/8 bg-[#030A16]/50 p-4 float-soft" style={{ animationDelay: `${i * 0.4}s` }}>
                  <div className="text-[11px] font-mono uppercase tracking-wide" style={{ color: d.color }}>{d.name}</div>
                  <div className="mt-3 flex -space-x-2">
                    {EMPLOYEES.filter((e) => e.department === d.id).slice(0, 3).map((e) => (
                      <img key={e.id} src={e.avatar} alt={e.name} className="h-9 w-9 rounded-lg border-2 border-[#030A16] object-cover" />
                    ))}
                    {EMPLOYEES.filter((e) => e.department === d.id).length === 0 && <span className="text-[11px] text-slate-600">Ready to staff</span>}
                  </div>
                  <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full" style={{ width: `${d.workload}%`, background: d.color }} /></div>
                </motion.div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-4">
              <img src={EMPLOYEES[0].avatar} alt="Ava" className="h-12 w-12 rounded-xl object-cover" />
              <div><div className="text-sm text-white">Ava · Administration Assistant</div><div className="text-xs text-slate-400">"Two decisions need your approval."</div></div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
